import FuseUtils from '@fuse/utils/FuseUtils';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import jwtServiceConfig from './jwtServiceConfig';
import { API } from 'app/configs/api';
import { getUserDeviceInfo } from 'src/app/helpers/utils';
import moment from 'moment';

class JwtService extends FuseUtils.EventEmitter {
  init() {
    this.setInterceptors();
    this.handleAuthentication();
  }

  isRefreshing = false;
  failedQueue = [];

  setInterceptors = () => {
    // Request interceptor: add access token and base URL
    axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('jwt_access_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      config.headers['Accept-Language'] = localStorage.getItem('language') || 'uz';
      // Prepend base API URL if not already present
      const baseUrl = process.env.REACT_APP_BASE_API_URL;
      if (baseUrl && config.url && !config.url.startsWith(baseUrl)) {
        config.url = baseUrl.replace(/\/$/, '') + '/' + config.url.replace(/^\//, '');
      }
      return config;
    });

    // Response interceptor: handle 401 and refresh token queue
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            if (!this.isRefreshing) {
              this.isRefreshing = true;
              try {
                const refreshResponse = await axios.post(API.refreshAccessToken, { refreshToken });
                const data = refreshResponse.data;
                if (data && data.token) {
                  localStorage.setItem('jwt_access_token', data.token);
                  localStorage.setItem('refresh_token', data.refreshToken);
                  localStorage.setItem('refresh_token_exp', data.refreshTokenExpiration);
                  this.processQueue(null, data.token);
                  originalRequest.headers['Authorization'] = `Bearer ${data.token}`;
                  return axios(originalRequest);
                } else {
                  this.processQueue(new Error('Refresh failed'), null);
                  this.logout();
                  throw new Error('Refresh failed');
                }
              } catch (e) {
                this.processQueue(e, null);
                this.logout();
                return Promise.reject(e);
              } finally {
                this.isRefreshing = false;
              }
            } else {
              // В очередь
              return new Promise((resolve, reject) => {
                this.failedQueue.push({
                  resolve: (token) => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    resolve(axios(originalRequest));
                  },
                  reject: (err) => {
                    reject(err);
                  },
                });
              });
            }
          }
        }
        return Promise.reject(error);
      }
    );
  };

  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  handleAuthentication = () => {
    const access_token = this.getAccessToken();
    if (!access_token) {
      this.emit('onNoAccessToken');
      return;
    }

    if (this.isAuthTokenValid(access_token)) {
      this.setSession(access_token);
      this.emit('onAutoLogin', true);
    } else {
      // access token expired, check refresh token
      const refresh_token = this.getRefreshToken();
      const refresh_token_exp = this.getRefreshTokenExpirationDate();
      let isRefreshTokenValid = false;
      if (refresh_token && refresh_token_exp) {
        try {
          isRefreshTokenValid = moment(refresh_token_exp).diff(moment()) > 0;
        } catch (error) {
          console.error(error);
        }
      }
      if (isRefreshTokenValid) {
        // try to refresh access token
        axios
          .post(API.refreshAccessToken, { refreshToken: refresh_token })
          .then((response) => {
            const data = response.data;
            if (data && data.token) {
              this.setSession(data.token);
              this.setRefreshToken(data.refreshToken);
              this.setRefreshTokenExpirationDate(data.refreshTokenExpiration);
              this.emit('onAutoLogin', true);
            } else {
              this.setSession(null);
              this.emit('onAutoLogout', 'Log in again, please');
            }
          })
          .catch(() => {
            this.setSession(null);
            this.emit('onAutoLogout', 'Log in again, please');
          });
      } else {
        this.setSession(null);
        this.emit('onAutoLogout', 'Log in again, please');
      }
    }
  };

  createUser = (data) => {
    return new Promise((resolve, reject) => {
      axios.post(jwtServiceConfig.signUp, data).then((response) => {
        if (response.data.user) {
          this.setSession(response.data.access_token);
          this.setRefreshToken(response.data.refreshToken);
          this.setRefreshTokenExpirationDate(response.data.refreshTokenExpiration);
          resolve(response.data.user);
          this.emit('onLogin', response.data.user);
        } else {
          reject(response.data.error);
        }
      });
    });
  };

  signInWithEmailAndPassword = (data) => {
    const deviceInfo = getUserDeviceInfo();
    return axios
      .post(API.login, {
        deviceInfo,
        deviceType: 10345134,
        ...data,
      })
      .then((response) => {
        if (response.data.token) {
          this.setSession(response.data.token);
          this.setRefreshToken(response.data.refreshToken);
          this.setRefreshTokenExpirationDate(response.data.refreshTokenExpiration);
          this.handleAuthentication();
        }
        return response.data;
      });
  };

  signInWithToken = () => {
    return new Promise((resolve, reject) => {
      axios
        .get(API.profile, {
          headers: {
            'Accept-Language': localStorage.getItem('language') || 'uz',
          },
        })
        .then((response) => {
          if (response.data) {
            this.setSession(localStorage.jwt_access_token);
            resolve(response.data);
          } else {
            this.logout();
            reject(new Error('Failed to login with token.'));
          }
        })
        .catch((error) => {
          this.logout();
          reject(new Error('Failed to login with token.'));
        });
    });
  };

  refreshToken = () => {
    const refreshToken = this.getRefreshToken();
    axios
      .post(API.refreshAccessToken, { refreshToken })
      .then((response) => {
        if (response.data) {
          this.setSession(response.data.token);
          this.setRefreshToken(response.data.refreshToken);
          this.setRefreshTokenExpirationDate(response.data.refreshTokenExpiration);
        } else {
          this.logout();
        }
      })
      .catch((error) => {
        console.error(error);
        this.logout();
      });
  };

  updateUserData = (user) => {
    return axios.post(jwtServiceConfig.updateUser, {
      user,
    });
  };

  setSession = (access_token) => {
    if (access_token) {
      localStorage.setItem('jwt_access_token', access_token);
      axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    } else {
      localStorage.removeItem('jwt_access_token');
      delete axios.defaults.headers.common.Authorization;
    }
  };

  setRefreshToken = (refresh_token) => {
    if (refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
    } else {
      localStorage.removeItem('refresh_token');
    }
  };

  setRefreshTokenExpirationDate = (date) => {
    if (date) {
      localStorage.setItem('refresh_token_exp', date);
    } else {
      localStorage.removeItem('refresh_token_exp');
    }
  };

  logout = () => {
    const language = localStorage.getItem('language');
    const isDarkMode = localStorage.getItem('isDarkMode');
    axios
      .post(API.logout)
      .then((response) => {
        if (response.status === 200) {
          console.clear();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        localStorage.clear();
        localStorage.setItem('language', language);
        localStorage.setItem('isDarkMode', isDarkMode);
        this.emit('onLogout', 'Logged out');
      });
  };

  isAuthTokenValid = (access_token) => {
    if (!access_token) {
      return false;
    }
    const decoded = jwtDecode(access_token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      // console.warn('access token expired');
      return false;
    }

    return true;
  };

  getAccessToken = () => {
    return window.localStorage.getItem('jwt_access_token');
  };

  getRefreshToken = () => {
    return window.localStorage.getItem('refresh_token');
  };

  getRefreshTokenExpirationDate = () => {
    return window.localStorage.getItem('refresh_token_exp');
  };
}

const instance = new JwtService();

export default instance;
